import { type ReactNode, useState } from "react"
import type { Hotkey } from "@tanstack/react-hotkeys"
import {
  Check,
  DotsThreeVertical,
  LinkSimple,
  PencilSimple,
  Star,
  Trash,
} from "@phosphor-icons/react"
import { toast } from "react-toastify"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { Tooltip } from "~/components/reusable/Tooltip"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { VIEW_SHORTCUTS } from "~/constants/hotkeys"
import { Badge, type BadgeVariant } from "~/components/reusable/Badge"
import { DeleteView, ManageView, ViewScopeIcon } from "~/components/planning/TableViewSelector"
import { ViewDetailsHoverCard } from "~/components/planning/ViewDetailsHoverCard"
import { canModifySavedView } from "~/server/api/utils/viewAccess"
import { type SavedTableView } from "~/server/api/routers/tableView"
import { useUser } from "~/providers/userProvider"
import { HAKIO_ONLY_TOOLTIP } from "~/constants/hakioOnly"
import { type FilterFieldType } from "~/utils/filter"
import { type PresetView } from "~/components/planning/presetView"
import { buildViewLink, type ViewTarget } from "~/hooks/useViewRoute"
import { getViewDetailsSubject } from "~/utils/viewDetailsVisibility"
import { cn } from "~/utils/helpers"

type ViewPillProps = {
  label: string
  isSelected: boolean
  onClick: () => void
  view?: SavedTableView
  onDeleted?: () => void
  tableIdOverride?: string
  /** The table's resolved id, sent as `Table ID` with `View Deleted`. */
  gridTableId?: string
  disabled?: boolean
  tooltip?: string
  /** Hakio-staff-only pill: rendered orange with a staff tooltip (same treatment as HeaderTabs). */
  hakioOnly?: boolean
  icon?: ReactNode
  /** Count/short text badge rendered after the label (e.g. items needing action). */
  badge?: string | number
  /** Badge color; defaults to "yellow" (attention). */
  badgeVariant?: BadgeVariant
  /**
   * Page-specific filter field *definitions* (label/type/icon), appended to the global
   * `filterFields` catalog. These are NOT applied filters — they exist so the details hover
   * card can resolve a saved filter whose field only exists on one page. Today the only such
   * field is the Fashion buy/supply plan's "SKU Size" (`skuSizeFilterField`): without it, a
   * view saved with a SKU-Size filter would render as "Unknown filter" with a generic icon.
   * Undefined on every other page (no page-specific fields → nothing to resolve).
   */
  extraFilterFields?: FilterFieldType[]
  /** The built-in view behind a preset pill. With `details`, it gets the details hover card. */
  preset?: PresetView
  /**
   * Enables the "Default" item in the "…" menu. When set, even
   * a system/preset pill (no `view`) renders the menu — with ONLY the Default option. Saved-view
   * pills additionally keep Edit/Delete.
   */
  showDefaultOption?: boolean
  /** Whether this pill is the user's current default view — renders a checkmark. */
  isDefault?: boolean
  /** Toggle this pill as the user's default (set when off, revert to system default when on). */
  onToggleDefault?: () => void
  /**
   * What a shared link should open. Enables "Copy link" in the "…" menu — like Default, it
   * also gives a preset pill a menu of its own. Only
   * targets a recipient can resolve: the Default pill is excluded, since a link without a `view`
   * param lands on whatever default the recipient has set rather than on Default itself.
   */
  linkTarget?: NonNullable<ViewTarget>
  /** Whether this pill is in the user's sidebar favorites — renders a filled star. */
  isFavorite?: boolean
  /** Toggles this pill in sidebar favorites. Omit to hide the item. */
  onToggleFavorite?: () => void
  /** Called once "Copy link" has written the link to the clipboard. */
  onLinkCopied?: () => void
  /** The `V` hotkeys act on this pill: its menu items show them on hover */
  shortcutsActive?: boolean
}

export const copyViewLink = async (target: NonNullable<ViewTarget>, onCopied?: () => void) => {
  try {
    await navigator.clipboard.writeText(buildViewLink(target, window.location.href))
    onCopied?.()
    toast.success("Link copied to clipboard")
  } catch {
    toast.error("Could not copy the link")
  }
}

/** Wraps a menu item in its `V` hotkey hint when the hotkeys act on this pill */
const withShortcut = (
  active: boolean,
  label: string,
  sequence: readonly Hotkey[],
  item: ReactNode,
) =>
  active ? (
    <ShortcutTooltip label={label} shortcutSequence={sequence} side="right">
      {item}
    </ShortcutTooltip>
  ) : (
    item
  )

/**
 * Selectable pill for one saved / preset table view — applies the view on click, with an
 * optional options menu (Edit / Delete / Default) and a details hover card for saved views.
 * Canonical intent: {@link ../design-system/intents.ts VIEW_PILL_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export const ViewPill = ({
  label,
  isSelected,
  onClick,
  view,
  onDeleted,
  tableIdOverride,
  gridTableId,
  disabled = false,
  tooltip,
  hakioOnly = false,
  icon,
  badge,
  badgeVariant = "yellow",
  extraFilterFields,
  preset,
  showDefaultOption = false,
  isDefault = false,
  onToggleDefault,
  linkTarget,
  isFavorite = false,
  onToggleFavorite,
  onLinkCopied,
  shortcutsActive = false,
}: ViewPillProps) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [hoverOpen, setHoverOpen] = useState(false)
  const { permissions, userId } = useUser()
  const cardSubject = getViewDetailsSubject({ view, preset, disabled })
  const hoverCardEnabled = cardSubject !== null

  const base =
    "group relative flex min-w-14 shrink-0 items-center justify-center gap-1.5 rounded-full h-7 px-3 label-base whitespace-nowrap transition"
  const selected = "bg-base text-fg-base shadow-card-rest"
  const unselected =
    "bg-transparent border border-base text-fg-subtle hover:bg-subtle-hover hover:text-fg-base"
  const disabledClass = "bg-transparent border border-base text-fg-subtle opacity-50"

  const showDefaultItem = showDefaultOption && !!onToggleDefault && !disabled
  const showCopyLinkItem = !!linkTarget && !disabled
  // A personal view isn't visible to anyone else, so the link would land the recipient on their
  // own default. The item stays visible, disabled, rather than silently disappearing.
  const copyLinkBlockedReason = view?.type === "personal" ? "Only you can see this view" : undefined
  const showFavoriteItem = !!onToggleFavorite && !disabled
  const hasMenu = (!!view || showDefaultItem || showCopyLinkItem || showFavoriteItem) && !disabled
  // Same treatment as HeaderTabs' hakioOnly tabs: orange text/icon + staff tooltip.
  const hakioOnlyClass = hakioOnly && "text-orange-600 [&_svg]:fill-orange-600"
  const resolvedTooltip = hakioOnly ? HAKIO_ONLY_TOOLTIP : tooltip
  const menuItemClass =
    "w-full rounded-md flex flex-row items-center gap-2 bg-base hover:bg-base-hover cursor-pointer label-base px-2 py-1.5"

  if (disabled) {
    const pill = (
      <div
        className={cn(base, disabledClass, "cursor-not-allowed", hakioOnlyClass)}
        aria-disabled="true"
      >
        <span className="truncate">{label}</span>
        {badge !== undefined && (
          <Badge variant={badgeVariant} size="sm">
            {badge}
          </Badge>
        )}
      </div>
    )
    return resolvedTooltip ? <Tooltip title={resolvedTooltip}>{pill}</Tooltip> : pill
  }

  const pill = (
    <div
      className={cn(base, "cursor-pointer", isSelected ? selected : unselected, hakioOnlyClass)}
      onClick={onClick}
      role="button"
      // Focusable and Enter/Space-activated whatever the hover card does: the pill switches the view
      // either way, and tying the tab stop to `hoverCardEnabled` left the Total/Pre-orders/Budget
      // switcher reachable by mouse only. Not a <button>: the menu below renders one inside this.
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return
        e.preventDefault()
        onClick()
      }}
    >
      <ViewScopeIcon type={view?.type} withTooltip={!hoverCardEnabled} />
      {icon && <span className="flex items-center shrink-0">{icon}</span>}
      <span className="truncate">{label}</span>
      {badge !== undefined && (
        <Badge variant={badgeVariant} size="sm">
          {badge}
        </Badge>
      )}
      {hasMenu && (
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                "absolute right-0.75 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-md py-0.5 transition-opacity cursor-pointer",
                // Inset right-side affordance, revealed on hover over the label's tail; kept small
                // so it doesn't dominate short pills, with an invisible ~32px hit target for an
                // accessible tap area.
                "after:absolute after:left-1/2 after:top-1/2 after:size-8 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
                "bg-medium hover:bg-medium-hover",
                menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100",
              )}
              onClick={(e) => e.stopPropagation()}
              aria-label="View options"
            >
              <DotsThreeVertical size={14} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[160px] rounded-[12px] p-0 overflow-hidden flex flex-col divide-y divide-border-base"
            side="bottom"
            align="end"
            sideOffset={6}
            onClick={(e) => e.stopPropagation()}
          >
            {view && (
              <div className="p-1">
                {canModifySavedView(permissions, view, userId) ? (
                  <ManageView
                    mode="update"
                    source="Pill"
                    view={view}
                    tableIdOverride={tableIdOverride}
                    trigger={
                      <div className={menuItemClass}>
                        <PencilSimple />
                        Edit view
                      </div>
                    }
                  />
                ) : (
                  <Tooltip title="You can only edit your own views">
                    <div className={cn(menuItemClass, "opacity-50 cursor-not-allowed")}>
                      <PencilSimple />
                      Edit view
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
            {showDefaultItem && (
              <div className="p-1">
                {withShortcut(
                  shortcutsActive,
                  isDefault ? "Clear default view" : "Set as default view",
                  VIEW_SHORTCUTS.toggleDefault.sequence,
                  <div
                    className={menuItemClass}
                    onClick={() => {
                      onToggleDefault?.()
                      setMenuOpen(false)
                    }}
                    role="button"
                    aria-label={isDefault ? "Remove default view" : "Set as default view"}
                  >
                    {isDefault ? <Check weight="bold" /> : <span className="size-4 shrink-0" />}
                    Default
                  </div>,
                )}
              </div>
            )}
            {showCopyLinkItem && (
              <div className="p-1">
                {copyLinkBlockedReason ? (
                  <Tooltip title={copyLinkBlockedReason}>
                    <div className={cn(menuItemClass, "opacity-50 cursor-not-allowed")}>
                      <LinkSimple />
                      Copy link
                    </div>
                  </Tooltip>
                ) : (
                  withShortcut(
                    shortcutsActive,
                    "Copy link to view",
                    VIEW_SHORTCUTS.copyLink.sequence,
                    <div
                      className={menuItemClass}
                      onClick={() => {
                        void copyViewLink(linkTarget, onLinkCopied)
                        setMenuOpen(false)
                      }}
                      role="button"
                      aria-label="Copy link to view"
                    >
                      <LinkSimple />
                      Copy link
                    </div>,
                  )
                )}
              </div>
            )}
            {showFavoriteItem && (
              <div className="p-1">
                {withShortcut(
                  shortcutsActive,
                  isFavorite ? "Remove from favorites" : "Favorite view",
                  VIEW_SHORTCUTS.favorite.sequence,
                  <div
                    className={menuItemClass}
                    onClick={() => {
                      onToggleFavorite?.()
                      setMenuOpen(false)
                    }}
                    role="button"
                    aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Star weight={isFavorite ? "fill" : "regular"} />
                    {isFavorite ? "Unfavorite" : "Favorite"}
                  </div>,
                )}
              </div>
            )}
            {view && (
              <div className="p-1">
                {canModifySavedView(permissions, view, userId) ? (
                  <DeleteView
                    view={view}
                    gridTableId={gridTableId}
                    onDelete={() => {
                      onDeleted?.()
                      setMenuOpen(false)
                    }}
                    customTrigger={
                      <div className={menuItemClass}>
                        <Trash className="text-red-600" />
                        <div className="text-red-600">Delete view</div>
                      </div>
                    }
                  />
                ) : (
                  <Tooltip title="You can only delete your own views">
                    <div className={cn(menuItemClass, "opacity-50 cursor-not-allowed")}>
                      <Trash className="text-red-600" />
                      <div className="text-red-600">Delete view</div>
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )

  if (!cardSubject) {
    // HeaderTabs shows the staff tooltip on enabled hakioOnly tabs too — mirror that here.
    return hakioOnly ? <Tooltip title={resolvedTooltip}>{pill}</Tooltip> : pill
  }

  // A preset card carries the staff-only line itself, so it replaces the hakioOnly tooltip.
  return (
    <ViewDetailsHoverCard
      {...cardSubject}
      extraFilterFields={extraFilterFields}
      // Suppress the details card while the options menu is open so they don't overlap.
      open={hoverOpen && !menuOpen}
      onOpenChange={setHoverOpen}
    >
      {pill}
    </ViewDetailsHoverCard>
  )
}
